import { DestroyRef, inject, signal, type Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Apollo } from 'apollo-angular';
import { finalize, map, type Observable } from 'rxjs';
import type { WidgetDataSource } from './widget-data-source';

/**
 * Normalized state exposed by the data-source runner to archetypes.
 * Every archetype reads these three signals to render loading / value / error
 * uniformly — no archetype reimplements subscription management (spec requirement:
 * "The runner exposes normalized loading, value, and error states").
 */
export interface WidgetDataSourceState<T> {
  /** True while the source is executing and no value has arrived yet. */
  readonly loading: Signal<boolean>;
  /** The latest mapped value from the source; `null` before first emission. */
  readonly value: Signal<T | null>;
  /** The last error thrown by the source; `null` when healthy. */
  readonly error: Signal<unknown>;
  /**
   * Cancels the active subscription immediately.
   *
   * Called by archetypes in `_run()` before starting a new run — prevents the
   * previous in-flight subscription from writing to stale signals after a
   * `refresh()` call replaces the state object.
   */
  readonly unsubscribe: () => void;
}

/**
 * Executes a `WidgetDataSource<T>` and returns normalized `loading / value /
 * error` signals plus an `unsubscribe()` handle.
 *
 * Must be called inside an Angular injection context (component constructor,
 * `runInInjectionContext`, etc.) because:
 *   - the `graphql` kind uses `inject(Apollo)`, and
 *   - `DestroyRef` is injected to auto-cancel on component destroy
 *     (`takeUntilDestroyed`), preventing subscription leaks after component
 *     teardown (C1 fix).
 *
 * Design D3: the `graphql` kind is the primary, auditable path; `observable`
 * is the escape hatch. Both paths converge on the same signal normalization so
 * archetypes are unaware of the underlying kind.
 */
export function runWidgetDataSource<T>(source: WidgetDataSource<T>): WidgetDataSourceState<T> {
  const destroyRef = inject(DestroyRef);
  const loading = signal(true);
  const value = signal<T | null>(null);
  const error = signal<unknown>(null);

  const sub = buildStream(source)
    .pipe(
      takeUntilDestroyed(destroyRef),
      finalize(() => loading.set(false)),
    )
    .subscribe({
      next: (v) => {
        value.set(v);
        loading.set(false);
      },
      error: (err: unknown) => {
        error.set(err);
        loading.set(false);
      },
    });

  return { loading, value, error, unsubscribe: () => sub.unsubscribe() };
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Builds the typed Observable for the given source kind.
 *
 * The `graphql` kind calls `apollo.query` directly — this is the justified
 * escape-hatch tier from `catalyst-data-layer`: the runner IS the data-layer
 * adapter for the widget port, so no higher-level fetcher or composable fits here.
 *
 * Apollo's `query()` resolves to an `ApolloQueryResult<unknown>`. We extract
 * `.data` and pass it through the BC-supplied `map` function to arrive at `T`.
 */
function buildStream<T>(source: WidgetDataSource<T>): Observable<T> {
  if (source.kind === 'observable') {
    return source.connect();
  }

  // graphql kind — inject Apollo inside the current injection context (the
  // caller already validated this via their `runInInjectionContext` / component).
  const apollo = inject(Apollo);

  return apollo
    .query<unknown>({
      query: source.document,
      variables: source.variables,
      fetchPolicy: source.fetchPolicy ?? 'no-cache',
    })
    .pipe(
      // ApolloQueryResult<unknown> → extract .data → apply the BC mapper → T
      map((result) => source.map(result.data)),
    );
}
